import { Link } from "react-router-dom";
import {
  Droplets, Zap, Flame, KeyRound, Hammer, ScanLine, CalendarClock, ReceiptText,
  BookOpen, Radar, Bot, ArrowRight, Check, ShieldCheck, Users,
} from "lucide-react";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import { Reveal, Overline } from "../components/Reveal";
import { GoldButton } from "../components/GoldButton";
import NotifyForm from "../components/NotifyForm";

const PROBLEMES = ["Fuite d'eau", "Panne électrique", "Problème de chauffage", "Serrure bloquée", "Équipement en panne"];

const PARCOURS = [
  "Vous décrivez votre besoin.",
  "Vous ajoutez des photos ou des vidéos si cela est utile.",
  "Vous choisissez le jour et l'heure souhaités parmi les créneaux disponibles.",
  "Votre demande est qualifiée.",
  "L'intervention est organisée avec le professionnel adapté.",
  "Vous suivez l'avancement en temps réel depuis l'application.",
  "Le compte-rendu est conservé dans votre dossier logement.",
];

const METIERS = [
  { icon: Droplets, t: "Plomberie", d: "Fuite, arrivée d'eau, évacuation ou équipement sanitaire : décrivez la situation et transmettez les éléments utiles." },
  { icon: Zap, t: "Électricité", d: "Panne, prise, interrupteur, éclairage : la demande est orientée vers le métier adapté." },
  { icon: Flame, t: "Chauffage, ventilation & climatisation", d: "Panne, équipement à contrôler ou maintenance à prévoir, signalée avec les informations du logement." },
  { icon: KeyRound, t: "Serrurerie & menuiserie", d: "Serrure, porte, fenêtre ou élément de menuiserie, selon les professionnels disponibles dans le secteur." },
  { icon: Hammer, t: "Petits travaux & entretien", d: "Petits travaux, entretien et maintenance disponibles dans le catalogue local." },
  { icon: ScanLine, t: "États des lieux", d: "États des lieux d'entrée et de sortie sous forme numérique : observations, photos, rapport et archivage." },
];

const INCLUS = ["La petite fourniture", "La main-d'œuvre", "Le nettoyage après intervention"];

const ABONNE = [
  "Le QR code logement : accès instantané aux informations et au signalement d'incident",
  "L'état des lieux intelligent inclus",
  "Le Carnet de Santé complet : historique, équipements, échéances",
  "2 prestations offertes chaque année pour les personnes âgées de votre famille",
];

export default function Depannage() {
  return (
    <div data-testid="depannage-page">
      <Seo
        title="Dépannage & prestations uniques — 7j/7"
        path="/depannage"
        description="Un dépannage sans abonnement : décrivez votre besoin, choisissez votre créneau et connaissez le prix avant de confirmer. Plomberie, électricité, chauffage, serrurerie sur Amiens et sa métropole."
      />
      <PageHero
        overline="Prestations uniques · Sans abonnement obligatoire"
        title={["Un dépannage ?", "On s'en occupe 7j/7"]}
        subtitle="Une fuite, une panne, une serrure bloquée ? Décrivez votre besoin, choisissez le jour et l'heure qui vous conviennent, et consultez le prix de la prestation avant de confirmer."
        testid="depannage-hero"
      />

      {/* Problèmes couverts */}
      <section className="relative z-10 mx-auto max-w-7xl px-5 pt-4 lg:px-8">
        <Reveal className="flex flex-wrap items-center gap-3">
          <span className="text-sm uppercase tracking-[0.2em] text-gold">Problèmes couverts</span>
          {PROBLEMES.map((p) => (
            <span key={p} className="rounded-full border border-white/15 px-4 py-2 text-sm text-slate-300">{p}</span>
          ))}
        </Reveal>
      </section>

      {/* Parcours */}
      <section className="relative z-10 mx-auto max-w-7xl px-5 py-24 lg:px-8">
        <Reveal><Overline>Le parcours est simple</Overline></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 max-w-2xl font-serif text-4xl tracking-tight text-white md:text-5xl">
            De l'incident à l'intervention, étape par étape.
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
          {PARCOURS.map((step, i) => (
            <Reveal key={i} delay={i * 0.05} className="bg-navy p-7">
              <span className="font-serif text-5xl text-gold/30">{String(i + 1).padStart(2, "0")}</span>
              <p className="mt-4 text-sm leading-relaxed text-slate-300">{step}</p>
            </Reveal>
          ))}
          <Reveal delay={0.35} className="flex items-center justify-center bg-navy p-7">
            <GoldButton to="/contact" variant="outline" data-testid="depannage-parcours-cta">
              Préparer une demande <ArrowRight className="h-4 w-4" />
            </GoldButton>
          </Reveal>
        </div>
      </section>

      {/* Prix connu à l'avance */}
      <section className="relative z-10 border-y border-white/10 bg-navy-surface/40 py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <Reveal>
              <div className="flex items-center gap-3 text-gold"><ReceiptText className="h-6 w-6" strokeWidth={1.5} /><Overline>Transparence</Overline></div>
              <h2 className="mt-5 font-serif text-4xl tracking-tight text-white md:text-5xl">Un prix connu avant de choisir</h2>
              <p className="mt-5 text-lg text-slate-300">
                Pas de mauvaise surprise : le prix ou l'estimation de la prestation est affiché avant la validation de votre demande.
              </p>
              <ul className="mt-7 space-y-3">
                {INCLUS.map((i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-200">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gold/15 text-gold"><Check className="h-3.5 w-3.5" strokeWidth={2.5} /></span>
                    {i} <span className="text-sm text-slate-500">— inclus lorsque prévu au catalogue</span>
                  </li>
                ))}
              </ul>
              <p className="mt-7 text-sm text-slate-400">
                Les cas particuliers, fournitures spécifiques ou travaux supplémentaires font l'objet d'une nouvelle validation ou d'un devis adapté. Aucun prélèvement n'est effectué au moment de la demande.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="rounded-3xl border border-gold/25 bg-navy-elevated p-8">
                <div className="flex items-center gap-3 text-gold"><CalendarClock className="h-6 w-6" strokeWidth={1.5} /><Overline>Pré-lancement</Overline></div>
                <p className="mt-4 text-slate-200">
                  Le catalogue complet des prestations et le prix de chaque intervention seront dévoilés dans l'application Speed Détresse au moment du lancement.
                </p>
                <p className="mt-3 text-sm text-slate-400">
                  Laissez-nous vos coordonnées pour être informé en priorité de l'ouverture et des tarifs.
                </p>
                <div className="mt-6"><NotifyForm /></div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Métiers */}
      <section className="relative z-10 mx-auto max-w-7xl px-5 py-24 lg:px-8">
        <Reveal><Overline>Des prestations pour les besoins du quotidien</Overline></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 max-w-3xl font-serif text-4xl tracking-tight text-white md:text-5xl">Chaque demande est orientée vers le bon professionnel.</h2>
        </Reveal>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {METIERS.map((m, i) => (
            <Reveal key={m.t} delay={(i % 3) * 0.08}>
              <div className="group h-full rounded-2xl border border-white/10 bg-navy-surface/60 p-7 transition-colors hover:border-gold/40" data-testid={`depannage-metier-${i}`}>
                <div className="flex h-12 w-12 items-center justify-center rounded-full border border-gold/40 text-gold transition-colors group-hover:bg-gold group-hover:text-navy">
                  <m.icon className="h-5 w-5" strokeWidth={1.5} />
                </div>
                <h3 className="mt-5 font-serif text-2xl text-white">{m.t}</h3>
                <p className="mt-3 text-sm text-slate-400">{m.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className="mt-6 text-sm text-slate-500">
            Rénovation, remplacement d'équipement et travaux importants : étude ou devis dédié.
          </p>
        </Reveal>
      </section>

      {/* Livret + suivi + IA */}
      <section className="relative z-10 border-y border-white/10 bg-navy-surface/40 py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-6 md:grid-cols-3">
            {[
              { icon: BookOpen, t: "Votre livret de dépannages", d: "Chaque intervention est conservée : dates, photos, comptes-rendus. La mémoire de tous vos dépannages, à chaque fois que vous faites appel à nous — même sans abonnement.", testid: "depannage-livret" },
              { icon: Radar, t: "Un suivi en temps réel", d: "Demande reçue, qualification, rendez-vous, intervention, compte-rendu : toutes les étapes sont suivies dans l'application, sans multiplier les appels et les messages.", testid: "depannage-suivi" },
              { icon: Bot, t: "L'IA prépare votre intervention", d: "Analyse de votre description et de vos photos, estimation de l'urgence, orientation vers le bon métier. L'IA accompagne le parcours — le diagnostic final reste celui du professionnel.", testid: "depannage-ia" },
            ].map((b, i) => (
              <Reveal key={b.t} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-white/10 bg-navy-surface/60 p-8" data-testid={b.testid}>
                  <div className="flex h-12 w-12 items-center justify-center rounded-full border border-gold/40 text-gold"><b.icon className="h-5 w-5" strokeWidth={1.5} /></div>
                  <h3 className="mt-5 font-serif text-2xl text-white">{b.t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{b.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Restaurants, bars & commerces */}
      <section className="relative z-10 mx-auto max-w-7xl px-5 py-24 lg:px-8" data-testid="depannage-commerces">
        <Reveal><Overline>Restaurants, bars & commerces</Overline></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 max-w-3xl font-serif text-4xl tracking-tight text-white md:text-5xl">
            En plein service, une fuite n'attend pas la fermeture.
          </h2>
        </Reveal>
        <div className="mt-14 grid items-center gap-10 lg:grid-cols-2">
          <Reveal>
            <figure className="overflow-hidden rounded-3xl border border-white/10">
              <img src="/images/story-resto-panne.webp" alt="Restaurateur face à une fuite en plein service, au téléphone" loading="lazy" className="aspect-[4/3] w-full object-cover" />
              <figcaption className="bg-navy-surface/60 p-6">
                <p className="text-sm text-slate-300">
                  <span className="text-gold">Plein service, évier bouché, eau partout.</span> Le restaurateur ouvre l'application, décrit la situation en un clic — avec son abonnement, ou en prestation unique.
                </p>
              </figcaption>
            </figure>
          </Reveal>
          <Reveal delay={0.12}>
            <figure className="overflow-hidden rounded-3xl border border-white/10">
              <img src="/images/story-resto-tech.webp" alt="Technicien Speed Détresse intervenant dans la cuisine du restaurant" loading="lazy" className="aspect-[4/3] w-full object-cover" />
              <figcaption className="bg-navy-surface/60 p-6">
                <p className="text-sm text-slate-300">
                  <span className="text-gold">Le technicien vérifié arrive.</span> Intervention suivie dans l'application, prix connu à l'avance, compte-rendu archivé — le service continue.
                </p>
              </figcaption>
            </figure>
          </Reveal>
          <Reveal delay={0.18}>
            <figure className="overflow-hidden rounded-3xl border border-white/10">
              <img src="/images/story-hotel.webp" alt="Hôtel — signalement d'une fuite via la tablette" loading="lazy" className="aspect-[4/3] w-full object-cover" />
              <figcaption className="bg-navy-surface/60 p-6">
                <p className="text-sm text-slate-300">
                  <span className="text-gold">Hôtels : l'entretien sous contrat.</span> Une fuite signalée depuis la chambre, le technicien dispatché sans passer par la réception.
                </p>
              </figcaption>
            </figure>
          </Reveal>
          <Reveal delay={0.24}>
            <figure className="overflow-hidden rounded-3xl border border-white/10">
              <img src="/images/story-bar.webp" alt="Bar — tireuse qui fuit en soirée" loading="lazy" className="aspect-[4/3] w-full object-cover" />
              <figcaption className="bg-navy-surface/60 p-6">
                <p className="text-sm text-slate-300">
                  <span className="text-gold">Bars & commerces : la panne n'attend pas.</span> Tireuse, éclairage, serrure : une prestation unique organisée en quelques clics.
                </p>
              </figcaption>
            </figure>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <p className="mt-8 text-center text-sm text-slate-500">
            Restaurants, bars, fast-foods, hôtels, salons de coiffure, magasins : un dépannage ne doit jamais arrêter l'activité.
          </p>
        </Reveal>
      </section>

      {/* Unique vs Abonné */}
      <section className="relative z-10 mx-auto max-w-7xl px-5 py-24 lg:px-8">
        <Reveal><Overline>Prestation unique ou abonnement ?</Overline></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 max-w-3xl font-serif text-4xl tracking-tight text-white md:text-5xl">Deux façons de profiter de Speed Détresse.</h2>
          <p className="mt-4 max-w-2xl text-slate-400">
            Même sans abonnement, chaque prestation unique reste gérée et facilitée par l'application et l'intelligence artificielle.
          </p>
        </Reveal>
        <Reveal delay={0.15} className="mt-12 overflow-hidden rounded-3xl border border-white/10">
          <div className="grid md:grid-cols-[1fr_1fr_1fr]" data-testid="depannage-compare-table">
            <div className="hidden border-b border-white/10 bg-navy-surface/60 p-5 md:block" />
            <div className="border-b border-gold/25 bg-navy-elevated p-5">
              <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold"><ShieldCheck className="h-4 w-4" /> Abonné Speed Détresse</p>
            </div>
            <div className="border-b border-white/10 bg-navy-surface/60 p-5">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Sans abonnement</p>
            </div>
            {[
              ["Dépannage", "Dépannage visible inclus", "Prestations uniques au besoin"],
              ["Écosystème", "Accès à l'écosystème complet", "Gestion au cas par cas"],
              ["Mémoire du logement", "Cahier des charges & Carnet de Santé", "Documents liés à la prestation demandée"],
              ["QR code", "QR code permanent du logement", "QR code si prévu dans la prestation"],
              ["Historique", "Historique complet du logement", "Historique limité aux services utilisés"],
              ["Devis & partenaires", "Selon l'offre, avec entreprises partenaires", "Selon la demande"],
              ["Marketplace", "Accès à la marketplace entre propriétaires abonnés", "Pas d'accès à la marketplace privée"],
            ].map(([label, abo, libre], i) => (
              <div key={label} className="contents">
                <div className={`p-5 text-sm font-medium text-white ${i % 2 ? "bg-navy-surface/40" : "bg-navy"}`}>{label}</div>
                <div className={`p-5 text-sm text-slate-200 ${i % 2 ? "bg-navy-elevated/60" : "bg-navy-elevated/40"}`}>{abo}</div>
                <div className={`p-5 text-sm text-slate-400 ${i % 2 ? "bg-navy-surface/40" : "bg-navy"}`}>{libre}</div>
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="mt-8 grid gap-6 md:grid-cols-2">
            <div className="rounded-3xl border border-gold/30 bg-navy-elevated p-8" data-testid="depannage-abo-card">
              <h3 className="font-serif text-2xl text-white">Speed Détresse — l'écosystème complet du logement</h3>
              <p className="mt-3 text-sm text-slate-300">Un abonnement qui transforme vos mètres carrés en sérénité.</p>
              <ul className="mt-5 space-y-3 text-sm text-slate-200">
                {ABONNE.map((l) => (
                  <li key={l} className="flex gap-3"><Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-gold" strokeWidth={2} />{l}</li>
                ))}
              </ul>
              <p className="mt-5 flex items-start gap-2 text-xs text-slate-400">
                <Users className="mt-0.5 h-4 w-4 flex-shrink-0 text-gold" />
                L'offre famille : vos deux prestations annuelles offertes peuvent profiter aux personnes âgées de votre famille.
              </p>
              <p className="mt-3 text-xs text-slate-500">
                Dépannage visible : robinetterie, fuite de flexible, serrure, ouverture/fermeture de porte, équipement en panne, anomalie visible…
                Les meubles concernés sont ceux installés par le propriétaire ou le bailleur lors de la mise en location, et non ceux montés par le locataire.
              </p>
              <div className="mt-7"><GoldButton to="/tarifs">Estimer mon abonnement <ArrowRight className="h-4 w-4" /></GoldButton></div>
            </div>
            <div className="flex flex-col justify-center rounded-3xl border border-white/10 bg-navy-surface/60 p-8" data-testid="depannage-unique-card">
              <h3 className="font-serif text-2xl text-white">Sans abonnement, les prestations uniques restent accessibles</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-300">
                État des lieux d'entrée ou de sortie, intervention technique, dépannage ponctuel, gestion des clés,
                intervention liée à une location saisonnière : commandez une prestation quand vous en avez besoin.
              </p>
              <div className="mt-7"><GoldButton to="/contact" variant="outline">Préparer une demande <ArrowRight className="h-4 w-4" /></GoldButton></div>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.25}>
          <p className="mt-8 text-center text-sm text-slate-500">
            Particuliers, propriétaires, bailleurs, agences et hôtes Airbnb : les prestations uniques répondent à un besoin précis, sans souscrire d'abonnement.
          </p>
        </Reveal>
      </section>

      {/* CTA final */}
      <section className="relative z-10 mx-auto max-w-7xl px-5 pb-28 lg:px-8">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl border border-gold/25 bg-navy-elevated px-8 py-14 text-center md:px-16">
            <div className="absolute inset-0 gold-radial" />
            <div className="relative">
              <h2 className="mx-auto max-w-2xl font-serif text-3xl tracking-tight text-white md:text-4xl">
                Venez découvrir l'application Speed Détresse
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-slate-300">
                Simplifiez le dépannage, le suivi et l'entretien de vos logements. Soyez informé de l'ouverture et des tarifs du catalogue.
              </p>
              <div className="mx-auto mt-8 max-w-xl"><NotifyForm /></div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
